// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { memoizeFunction } from "@fluentui/react";

import { isSharedUXDarkModeOn, isThemingEnabled } from "@1js/acui-theme";

import { defaultAppColorPalette } from "./appColorPalette";

export interface ButtonSemanticColors {
  defaultBackgroundColor: string;
  hoverBackgroundColor: string;
  pressedBackgroundColor: string;
  toggledBackgroundColor: string;
  disabledBackgroundColor: string;
  focusedBackgroundColor: string;
  textColor: string;
  textHoverColor?: string;
  textPressedColor?: string;
  textToggledColor?: string;
  textDisabledColor: string;
  disabledBorderColor: string;
  focusedBorderColor: string;
  focusDisabledBackgroundColor: string;
  focusDisabledBorderColor: string;
  borderColor?: string;
  borderHoverColor?: string;
  borderPressedColor?: string;
  borderToggledColor?: string;
  placeholderTextColor?: string;
  itemDividerColor?: string;
}
export interface ActionControlSemanticColors extends ButtonSemanticColors {
  tabHeaderBackgroundColor: string;
  tabHeaderHoverBackgroundColor: string;
  tabHeaderPressedBackgroundColor: string;
  tabHeaderFocusedBorderColor: string;

  activeTabHeaderBackgroundColor: string;
  activeTabHeaderTextColor: string;
  activeTabHeaderHoverBackgroundColor: string;
  activeTabHeaderHoverTextColor: string;
  activeTabHeaderPressedBackgroundColor: string;
  activeTabHeaderPressedTextColor: string;

  contextualTabHeaderBackgroundColor: string;
  contextualTabHeaderTextColor: string;
  contextualTabHeaderHoverBackgroundColor: string;

  tellMeControlActiveBackgroundColor: string;
  tellMeControlDefaultBackgroundColor: string;
  tellMeControlBorderColor: string; // Only used for Tell Me in Header
}

const actionControlSemanticColors: ActionControlSemanticColors = {
  defaultBackgroundColor: defaultAppColorPalette.gray20,
  hoverBackgroundColor: defaultAppColorPalette.gray30,
  pressedBackgroundColor: defaultAppColorPalette.gray50,
  toggledBackgroundColor: defaultAppColorPalette.gray40,
  disabledBackgroundColor: defaultAppColorPalette.gray20,
  focusedBackgroundColor: "transparent",
  textColor: defaultAppColorPalette.gray180,
  textDisabledColor: defaultAppColorPalette.gray90,
  disabledBorderColor: defaultAppColorPalette.gray20,
  focusedBorderColor: defaultAppColorPalette.gray130,
  focusDisabledBackgroundColor: "transparent",
  focusDisabledBorderColor: defaultAppColorPalette.black,
  placeholderTextColor: defaultAppColorPalette.gray130,

  tabHeaderBackgroundColor: defaultAppColorPalette.gray20,
  tabHeaderHoverBackgroundColor: defaultAppColorPalette.gray30,
  tabHeaderPressedBackgroundColor: defaultAppColorPalette.gray50,
  tabHeaderFocusedBorderColor: defaultAppColorPalette.gray130,

  activeTabHeaderBackgroundColor: defaultAppColorPalette.gray20,
  activeTabHeaderTextColor: defaultAppColorPalette.gray180,
  activeTabHeaderHoverBackgroundColor: defaultAppColorPalette.gray30,
  activeTabHeaderHoverTextColor: defaultAppColorPalette.gray180,
  activeTabHeaderPressedBackgroundColor: defaultAppColorPalette.gray50,
  activeTabHeaderPressedTextColor: defaultAppColorPalette.gray180,

  contextualTabHeaderBackgroundColor: defaultAppColorPalette.white,
  /**
   * contextualTabHeaderTextColor without the theme is appColors.primary, but we have no access to appColors here
   * Usage of this will be guarded by the theming flight, and if not on should use appColors directly
   */
  contextualTabHeaderTextColor: "",
  contextualTabHeaderHoverBackgroundColor: defaultAppColorPalette.gray30,

  tellMeControlActiveBackgroundColor: defaultAppColorPalette.white,
  tellMeControlDefaultBackgroundColor: defaultAppColorPalette.gray20,
  tellMeControlBorderColor: defaultAppColorPalette.white,
};

const actionControlSemanticColorsWithTheme = memoizeFunction(
  (theme: ITheme): ActionControlSemanticColors => {
    return {
      defaultBackgroundColor: theme.palette.neutralLighterAlt,
      hoverBackgroundColor: theme.palette.neutralLighter,
      pressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
      toggledBackgroundColor: theme.palette.neutralLight,
      disabledBackgroundColor: theme.palette.neutralLighterAlt,
      focusedBackgroundColor: "transparent",
      textColor: theme.palette.neutralDark,
      textDisabledColor: theme.palette.neutralTertiaryAlt,
      disabledBorderColor: theme.palette.neutralLighterAlt,
      focusedBorderColor: theme.palette.neutralPrimaryAlt,
      focusDisabledBackgroundColor: "transparent",
      focusDisabledBorderColor: theme.palette.neutralDark,
      placeholderTextColor: theme.palette.neutralPrimaryAlt,

      tabHeaderBackgroundColor: theme.palette.neutralLighterAlt,
      tabHeaderHoverBackgroundColor: theme.palette.neutralLighter,
      tabHeaderPressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
      tabHeaderFocusedBorderColor: theme.palette.neutralPrimaryAlt,

      activeTabHeaderBackgroundColor: theme.palette.neutralLighterAlt,
      activeTabHeaderTextColor: theme.palette.neutralDark,
      activeTabHeaderHoverBackgroundColor: theme.palette.neutralLighter,
      activeTabHeaderHoverTextColor: theme.palette.neutralDark,
      activeTabHeaderPressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
      activeTabHeaderPressedTextColor: theme.palette.neutralDark,

      contextualTabHeaderBackgroundColor: theme.palette.white,
      contextualTabHeaderTextColor: theme.palette.themePrimary,
      contextualTabHeaderHoverBackgroundColor: theme.palette.neutralLighter,

      tellMeControlActiveBackgroundColor: theme.palette.white,
      tellMeControlDefaultBackgroundColor: theme.palette.neutralLighterAlt,
      tellMeControlBorderColor: theme.palette.white,

      itemDividerColor: theme.palette.neutralQuaternary,
    };
  }
);

const darkModeActionControlSemanticColors = memoizeFunction(
  (theme: ITheme): ActionControlSemanticColors => {
    return {
      ...actionControlSemanticColorsWithTheme(theme),
      contextualTabHeaderTextColor: theme.palette.themeDark,
      tellMeControlActiveBackgroundColor: theme.palette.neutralQuaternaryAlt,
      tellMeControlDefaultBackgroundColor: theme.palette.neutralQuaternaryAlt,
    };
  }
);

export const getActionControlSemanticColorsWithTheme = (
  theme: ITheme
): ActionControlSemanticColors => {
  if (!isThemingEnabled() || theme === null || theme === undefined) {
    return actionControlSemanticColors;
  }
  if (isSharedUXDarkModeOn()) {
    return darkModeActionControlSemanticColors(theme);
  }
  return actionControlSemanticColorsWithTheme(theme);
};
